import { useEffect, useState } from "react";
import { getAuthUser, getStoredAccountType } from "@/modules/safecheck/auth";
import {
  fetchScopeCompanies,
  fetchScopeHospitals,
  type ScopeHospital,
} from "@/modules/safecheck/api/insurerApi";
import { getWorkContext, setWorkContext } from "@/modules/safecheck/workContext";

const selectClass =
  "h-11 max-w-[8.5rem] cursor-pointer sm:max-w-[14rem] rounded-xl border border-border bg-card px-3 text-sm font-medium text-foreground outline-none transition-[background-color,border-color,box-shadow] duration-150 hover:border-primary/30 focus:border-accent focus:ring-4 focus:ring-accent/25";

/** Recharge la page pour que tous les écrans relisent les données du nouveau contexte. */
function applyAndReload(patch: Parameters<typeof setWorkContext>[0]): void {
  setWorkContext(patch);
  window.location.reload();
}

/**
 * Contexte de travail dans l'en-tête :
 * - Assurance : entreprise consultée ;
 * - Sinistre : assureur (si plusieurs) puis prestataire ;
 * - Audit : assureur (si plusieurs).
 */
export function WorkContextSelector() {
  const accountType = getStoredAccountType();
  const user = getAuthUser();
  const insurers = user?.insurers ?? [];
  const context = getWorkContext();

  const [hospitals, setHospitals] = useState<ScopeHospital[]>([]);

  const isInsurerAccount = accountType === "enterprise";
  const isClaimsAccount = accountType === "hospital";
  const isAuditAccount = accountType === "auditor";
  // Admin : choix de l'assureur pour ses packages, gestionnaires (« Tous » = vues globales).
  const isAdminAccount = accountType === "admin";
  const needsInsurerChoice = ((isClaimsAccount || isAuditAccount) && insurers.length > 1) || (isAdminAccount && insurers.length > 0);
  const insurerReady = !needsInsurerChoice || context.insurerId !== null;

  useEffect(() => {
    if (!isInsurerAccount) return;
    let cancelled = false;
    fetchScopeCompanies()
      .then((rows) => {
        if (cancelled) return;
        const selected = getWorkContext().companyId;
        if (rows.length > 0 && !rows.some((row) => row.id === selected)) {
          applyAndReload({ companyId: rows[0].id });
        }
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [isInsurerAccount]);

  useEffect(() => {
    if (!isClaimsAccount || !insurerReady) return;
    let cancelled = false;
    fetchScopeHospitals()
      .then((rows) => {
        if (cancelled) return;
        setHospitals(rows);
        const selected = getWorkContext().hospitalId;
        if (rows.length > 0 && !rows.some((row) => row.id === selected)) {
          applyAndReload({ hospitalId: rows[0].id });
        }
      })
      .catch(() => setHospitals([]));
    return () => {
      cancelled = true;
    };
  }, [isClaimsAccount, insurerReady]);

  if (!isInsurerAccount && !isClaimsAccount && !isAuditAccount && !isAdminAccount) return null;

  return (
    <div className="flex min-w-0 items-center gap-2">
      {needsInsurerChoice ? (
        <select
          id="work-context-insurer"
          aria-label="Assureur"
          className={selectClass}
          value={context.insurerId ?? ""}
          onChange={(event) => applyAndReload({ insurerId: event.target.value ? Number(event.target.value) : null })}
        >
          <option value="">{isAdminAccount ? "Tous les assureurs" : "Choisir un assureur"}</option>
          {insurers.map((insurer) => (
            <option key={insurer.id} value={insurer.id}>
              {insurer.name}
            </option>
          ))}
        </select>
      ) : null}

      {/* Assurance : plus de liste visible ; la première entreprise du périmètre est choisie automatiquement (voir l'effet ci-dessus). */}

      {isClaimsAccount && insurerReady ? (
        <select
          id="work-context-hospital"
          aria-label="Prestataire"
          className={selectClass}
          value={context.hospitalId ?? ""}
          onChange={(event) => applyAndReload({ hospitalId: event.target.value ? Number(event.target.value) : null })}
        >
          {hospitals.length === 0 ? <option value="">Aucun prestataire dans le réseau</option> : null}
          {hospitals.map((hospital) => (
            <option key={hospital.id} value={hospital.id}>
              {hospital.name}
            </option>
          ))}
        </select>
      ) : null}
    </div>
  );
}
